import CustomTable from "../../../components/CustomTable/CustomTable";
import TableHeader from "../../../components/CustomTable/TableHeader";
import { DatePicker, Modal, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../../hooks/useSearchableTable";
import {
  transactionHistory,
  disputeRaised,
  rejectTxn,
  pendingTxn,
  suspenseTxn,
  markTransactionDuplicate,
} from "../../../redux/slices/PaymentsSlice";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { getPaymentColumns } from "./getPaymentColumns";
import { useNavigate } from "react-router-dom";
import { REASON_MODAL_CONFIG } from "../../../constants/constants";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ReasonForm from "../../../components/CustomForms/ReasonForm";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";
import { downloadPageHandle } from "../../../helpers/helpers";

const RangePicker = DatePicker?.RangePicker;

const PAYMENTS_FETCH_BY_TAB = {
  1: transactionHistory,
  2: disputeRaised,
  3: rejectTxn,
  4: pendingTxn,
  5: suspenseTxn,
};

const getPaymentsRowKey = (tabIndex) => {
  if (tabIndex === 1) return "appTransactionId";
  if (tabIndex === 5) return "transactionId";
  return "txnId";
};

const getPaymentsDynamicPageSize = () => {
  if (typeof window === "undefined" || !window.innerHeight) return 10;
  // Overhead: Navbar (70px) + layout padding (50px) + Header (58px)
  // + Table thead (44px) + Pagination bar (56px) + safety buffer (52px) = ~330px
  const availableHeight = window.innerHeight - 330;
  const calculated = Math.floor(availableHeight / 50);
  return Math.max(6, Math.min(15, calculated || 10));
};

const PaymentsTable = ({ data, tabIndex, canMarkDuplicate = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state?.payments || {});
  const { loading: docLoading = false, error: docError = null } = useSelector(
    (state) => state?.documents || {}
  );
  const [pagination, setPagination] = useState(() => ({
    page: 1,
    pageSize: getPaymentsDynamicPageSize(),
  }));
  const [modal, setModal] = useState({ type: null, data: null });
  const [columnControl, setColumnControl] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const prevTabIndexRef = useRef(tabIndex);

  useEffect(() => {
    const handleResize = () => {
      const nextSize = getPaymentsDynamicPageSize();
      setPagination((prev) =>
        prev.pageSize === nextSize
          ? prev
          : { page: 1, pageSize: nextSize }
      );
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const screenKey = useMemo(
    () => buildTableScreenKey("payments", String(tabIndex)),
    [tabIndex]
  );

  const rawRows = useMemo(
    () => (Array.isArray(data) ? data : data?.data || []),
    [data]
  );

  const hasDateFilter = Boolean(
    Array.isArray(dateRange) && (dateRange[0] || dateRange[1])
  );

  const dateFilteredRows = useMemo(() => {
    if (!hasDateFilter) return rawRows;

    const startMs = dateRange[0]
      ? new Date(dateRange[0]).setHours(0, 0, 0, 0)
      : null;
    const endMs = dateRange[1]
      ? new Date(dateRange[1]).setHours(23, 59, 59, 999)
      : null;

    return rawRows.filter((row) => {
      const rawDate = row?.createdAt || row?.transactionDate;
      if (!rawDate) return false;
      const rowMs = new Date(rawDate).getTime();
      if (Number.isNaN(rowMs)) return false;
      if (startMs !== null && rowMs < startMs) return false;
      if (endMs !== null && rowMs > endMs) return false;
      return true;
    });
  }, [rawRows, dateRange, hasDateFilter]);

  const { searchText, setSearchText, filteredData } =
    useSearchableTable(dateFilteredRows);

  const closeModal = useCallback(
    () => setModal({ type: null, data: null }),
    []
  );

  const openModal = useCallback((type, record) => {
    setModal({ type, data: record });
  }, []);

  // Client date filter only sees the current page — use filtered length when active.
  const total = hasDateFilter
    ? dateFilteredRows.length
    : data?.total ?? (Array.isArray(data) ? data.length : 0);

  useEffect(() => {
    if (docError) {
      message.error(docError || "Download failed");
    }
  }, [docError]);

  // Single fetch path. On tab change, reset to page 1 first so we never
  // dispatch once with the old page and again after the reset.
  useEffect(() => {
    if (!tabIndex) return;
    const action = PAYMENTS_FETCH_BY_TAB[tabIndex];
    if (!action) return;

    const tabChanged = prevTabIndexRef.current !== tabIndex;
    if (tabChanged) {
      prevTabIndexRef.current = tabIndex;
      setDateRange(null);
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
        return;
      }
    }

    dispatch(
      action({ page: pagination.page, pageSize: pagination.pageSize })
    );
  }, [dispatch, tabIndex, pagination.page, pagination.pageSize]);

  const columns = useMemo(
    () =>
      getPaymentColumns({
        tabIndex,
        canMarkDuplicate,
        onView: (id) => navigate(`/payments/payment-details/${id}/${tabIndex}`),
        onApprove: (record) => openModal("approve", record),
        onReject: (record) => openModal("reject", record),
        onMarkDuplicate: (record) => {
          Modal.confirm({
            title: "Mark as Duplicate",
            content: `Mark ${record.appTransactionId} (${
              record.txnChannel || "transaction"
            }) as Duplicate? Wallet and ledger will exclude this amount.`,
            okText: "Mark as Duplicate",
            onOk: async () => {
              const result = await dispatch(
                markTransactionDuplicate({
                  transactionId: record.appTransactionId,
                })
              );
              if (markTransactionDuplicate.fulfilled.match(result)) {
                message.success("Transaction marked as Duplicate");
                dispatch(
                  transactionHistory({
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                  })
                );
              } else {
                message.error(
                  result.payload || "Failed to mark transaction as Duplicate"
                );
              }
            },
          });
        },
      }),
    [
      canMarkDuplicate,
      dispatch,
      navigate,
      openModal,
      pagination.page,
      pagination.pageSize,
      tabIndex,
    ]
  );

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      page: newPagination.current || 1,
      pageSize: newPagination.pageSize || prev.pageSize,
    }));
  };

  const handleReasonSubmit = () => {};

  const handleDownloadPdf = useCallback(
    () =>
      downloadPageHandle({
        dispatch,
        type: "pdf",
        screenName: "payments",
        statusId: tabIndex,
      }),
    [dispatch, tabIndex]
  );

  const handleDownloadExcel = useCallback(
    () =>
      downloadPageHandle({
        dispatch,
        type: "excel",
        screenName: "payments",
        statusId: tabIndex,
      }),
    [dispatch, tabIndex]
  );

  const dateRangeControl = useMemo(() => {
    if (!RangePicker) return null;
    return (
      <RangePicker
        value={dateRange}
        onChange={setDateRange}
        format="DD/MM/YYYY"
        placeholder={["Start Date", "End Date"]}
        allowClear
        className="payments-date-range-picker"
        data-testid="payments-date-range-picker"
      />
    );
  }, [dateRange]);

  return (
    <div className="payments-table-section">
      <TableHeader
        searchText={searchText}
        setSearchText={setSearchText}
        columnControl={columnControl}
        extraControls={dateRangeControl}
        isDownloadRequired
        onDownloadPdf={handleDownloadPdf}
        onDownloadExcel={handleDownloadExcel}
        downloadLoading={docLoading}
      />

      <CustomTable
        rowKey={getPaymentsRowKey(tabIndex)}
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        scroll={false}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total,
        }}
        onChange={handleTableChange}
        screenKey={screenKey}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.payments}
        onColumnControlChange={setColumnControl}
        tableStyle="payments-custom-table"
      />

      {modal.type && (
        <CustomModal
          title={`${
            modal.type.charAt(0).toUpperCase() + modal.type.slice(1)
          } Transaction`}
          open
          centered
          content={
            <ReasonForm
              label={REASON_MODAL_CONFIG[modal.type]?.label}
              placeholder="Write a valid reason"
              btnName={REASON_MODAL_CONFIG[modal.type]?.btnName}
              btnType={modal.type === "reject" ? "danger" : "primary"}
              submitLoading={false}
              submitError=""
              onClose={closeModal}
              handleOnFormSubmit={handleReasonSubmit}
            />
          }
        />
      )}
    </div>
  );
};

export default PaymentsTable;
